import {VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {parseDate} from 'shared/web';

import {Attributes} from '../../components/Attributes/Attributes';
import {CommentMechanic} from '../../components/CommentMechanic/CommentMechanic';
import {ListFeatures} from '../../components/ListFeatures/ListFeatures';
import {Section} from '../../components/Section/Section';
import {TestDriveDataBundle} from '../../hooks/useGetDataForTestDrive';
import {i18n} from '../../i18n/i18n';
import {isCebiaWorkspace} from '../../utils/isCebiaWorkspace';
import {TestDriveContent} from './components/TestDriveContent';
import {TestDriveRouteMap} from './components/TestDriveRouteMap/TestDriveRouteMap';
import {hasTestDriveDetailedData} from './utils/hasTestDriveDetailedData';

type TestDriveProps = Omit<TestDriveDataBundle, 'sectionConfig'>;
export function TestDrive({testDrive}: TestDriveProps) {
  const formatDateTime = useDateTimeFormatter();

  if (!testDrive) {
    return null;
  }

  // testDrive.performed is a checkbox in DMS, so it's either 'true' or 'false', it does not have any additional data related to test drive
  // testDrive.title.testDriveDate is the date of actual test drive done in CarAudit application
  const wasTestDrivePerformed =
    testDrive.performed === 'true' || Boolean(testDrive.title.testDriveDate);

  const heading = wasTestDrivePerformed
    ? i18n.t('testDrivePerformed')
    : i18n.t('testDriveNotPerformed');
  const variant = wasTestDrivePerformed ? 'ready' : 'neutral';
  const description = wasTestDrivePerformed ? null : i18n.t('testDriveNotPerformedDescription');

  if (isCebiaWorkspace()) {
    return (
      <Section
        id="TESTDRIVE"
        icon="testDrive"
        heading={i18n.t('testDriveHeader')}
        header={null}
        directionOverride={['column', 'column', 'row', 'row']}
        content={
          <ListFeatures
            columns={[1, 2, 2, 2]}
            spacing={[6, 6, 10, 10]}
            features={[
              {
                title: heading,
                variant,
                flagTexts: [],
                value: testDrive.mechanic.comment,
                description,
              },
            ]}
          />
        }
      />
    );
  }

  const hasDetailedData = hasTestDriveDetailedData(testDrive);
  const showMechanicHeader =
    hasDetailedData &&
    Boolean(testDrive.mechanic.name) &&
    isNotNilOrEmpty(testDrive.mechanic.comment);

  return (
    <Section
      id="TESTDRIVE"
      icon="testDrive"
      heading={i18n.t('testDriveHeader')}
      header={
        // eslint-disable-next-line eag/prefer-show -- Pre-existing violation
        showMechanicHeader ? (
          <CommentMechanic comment={testDrive.mechanic.comment} name={testDrive.mechanic.name} />
        ) : null
      }
      directionOverride={['column', 'column', 'row', 'row']}
      sidebarWidthOverride={hasDetailedData ? ['100%', '100%', 100, 100] : undefined}
      sidebar={
        hasDetailedData ? (
          <Attributes
            data-testid="testDriveAttributes"
            rows={[
              {
                label: i18n.t('testDrive.date'),
                text: testDrive.title.testDriveDate
                  ? formatDateTime('dateShort', parseDate(testDrive.title.testDriveDate))
                  : null,
              },
              {label: i18n.t('testDrive.driver'), text: testDrive.title.driver},
            ]}
          />
        ) : undefined
      }
      content={
        hasDetailedData ? (
          <VStack spacing={[6, 6, 10, 10]}>
            <ListFeatures
              columns={[1, 2, 2, 2]}
              spacing={[6, 6, 10, 10]}
              features={[
                {
                  title: heading,
                  variant,
                  flagTexts: [],
                  value: showMechanicHeader ? null : testDrive.mechanic.comment,
                  description,
                },
              ]}
            />
            <TestDriveContent
              duration={testDrive.content.duration}
              driveStart={testDrive.content.driveStart}
              driveEnd={testDrive.content.driveEnd}
              distance={testDrive.content.distance}
              maxSpeed={testDrive.content.maxSpeed}
              avgSpeed={testDrive.content.avgSpeed}
              weakGpsSignal={testDrive.content.weakGpsSignal}
            />
          </VStack>
        ) : (
          <ListFeatures
            columns={[1, 2, 2, 2]}
            spacing={[6, 6, 10, 10]}
            features={[
              {
                title: heading,
                variant,
                flagTexts: [],
                value: testDrive.mechanic.comment,
                description,
              },
            ]}
          />
        )
      }
    >
      {/* A strip across the whole section, below the results of the drive. */}
      <TestDriveRouteMap encodedRoute={testDrive.content.route} data-testid="testDriveRoute" />
    </Section>
  );
}
